import PageContainer from '@Guidelines/Layout/PageContainer';
import PageHeader from '@Guidelines/Layout/PageHeader';
import WorkflowRunsContent from '@UI/Pages/WorkflowRuns/WorkflowRunsContent';
import * as React from 'react';
import Filters from '@UI/Pages/WorkflowRuns/Filters';
import { useDispatch, useStoreQuery } from '@verclam/synapse';
import fetchBeneficiaryDirectory from '@Application/Beneficiaries/BeneficiaryDirectory/fetchBeneficiaryDirectory';
import FetchBeneficiaryDirectory from '@Application/Beneficiaries/BeneficiaryDirectory/Queries/FetchBeneficiaryDirectory';
import fetchSubjectDirectory from '@Application/Subjects/SubjectDirectory/fetchSubjectDirectory';
import FetchSubjectDirectory from '@Application/Subjects/SubjectDirectory/Queries/FetchSubjectDirectory';
import fetchFacilityUsers from '@Application/Users/FacilityUsers/fetchFacilityUsers';
import FetchFacilityUsers from '@Application/Users/FacilityUsers/Queries/FetchFacilityUsers';
import useStrictCurrentFacility from '@UI/Providers/CurrentFacilityProvider/useStrictCurrentFacility';
import useStrictAuthenticatedUser from '@UI/Providers/AuthenticatedUserProvider/useStrictAuthenticatedUser';
import useHasPermission from '@UI/Providers/PermissionProvider/useHasPermission';
import { PermissionEnum } from '@Application/Security/Roles';
import WorkflowRunsNotifications from '@UI/Pages/WorkflowRuns/WorkflowRunsNotifications';
import { Button } from '@verclam/ui-kit';
import { RefreshCw } from 'lucide-react';
import useEffectiveWorkflowRunFilters from '@UI/Pages/WorkflowRuns/Hooks/useEffectiveWorkflowRunFilters';
import { BreadcrumbActions } from '@verclam/breadcrumbs';
import fetchFacilityWorkflowRuns from '@Application/WorkflowRuns/FacilityWorkflowRuns/fetchFacilityWorkflowRuns';
import FetchFacilityWorkflowRuns from '@Application/WorkflowRuns/FacilityWorkflowRuns/Queries/FetchFacilityWorkflowRuns';
import useRefetchInterval, {
	SUPERVISION_REFETCH_INTERVAL_IN_MILLISECONDS,
} from '@UI/Hooks/useRefetchInterval';
import useDebounce from '@UI/Hooks/useDebounce';
import useCurrentOrganizationName from '@UI/Providers/CurrentOrganizationProvider/useCurrentOrganizationName';

const REFERENT_SCOPE_PERMISSIONS = [
	PermissionEnum.WORKFLOW_RUN_FACILITY_READ,
	PermissionEnum.USER_READ,
] as const;

const FILTERS_DEBOUNCE_DELAY_IN_MILLISECONDS = 300;

/**
 * Single-screen feature, same shape as `Administration/AuditTrail/`: header, then
 * `WorkflowRunsContent`, which owns every fetch, renders `Filters` itself (it is the only place
 * that already holds the beneficiary directory and agency users those filters need) and every branch
 * of the list's display.
 */
const WorkflowRunsPage: React.FC = () => {
	const dispatch = useDispatch();
	const { currentFacility } = useStrictCurrentFacility();
	const { user } = useStrictAuthenticatedUser();
	const facilityId = currentFacility.id;
	const accountId = user.account?.id ?? null;
	const organizationName = useCurrentOrganizationName();

	const filters = useEffectiveWorkflowRunFilters();
	const debouncedFilters = useDebounce(filters, FILTERS_DEBOUNCE_DELAY_IN_MILLISECONDS);
	// A debounced value from another agency or caller is never sent: the fresh default applies at once.
	const listFilters = debouncedFilters.belongsTo(facilityId, user.id) ? debouncedFilters : filters;

	const canSeeAllUsers = useHasPermission(REFERENT_SCOPE_PERMISSIONS, 'all');
	// The screen is reserved to readers of the whole agency: the directory is never narrowed to a user.
	const assignedUserId = null;

	const [mountStamp] = React.useState(() => Date.now());
	const queryKey = `${facilityId}?${listFilters.queryParams.toString()}#${mountStamp}`;

	const { data: beneficiaryDirectory } = useStoreQuery({
		selector: (store) => store.beneficiariesStore.directory,
		stopOn: (value) =>
			value !== null && value.facilityId === facilityId && value.assignedUserId === assignedUserId,
		deps: [accountId, facilityId, assignedUserId],
		query: () => {
			// Without an account the directory stays empty: cards fall back to their default name.
			if (accountId === null) {
				return Promise.resolve(null);
			}

			return dispatch(
				fetchBeneficiaryDirectory(
					new FetchBeneficiaryDirectory(accountId, facilityId, assignedUserId),
				),
			);
		},
	});

	const { data: subjectDirectory } = useStoreQuery({
		selector: (store) => store.subjectsStore.directory,
		stopOn: (value) => value !== null && value.facilityId === facilityId,
		deps: [accountId, facilityId],
		query: () => {
			// Without an account the directory stays empty: cards fall back to their default name.
			if (accountId === null) {
				return Promise.resolve(null);
			}

			return dispatch(fetchSubjectDirectory(new FetchSubjectDirectory(accountId, facilityId)));
		},
	});

	const {
		isFetching,
		error: listError,
		refetch: refetchRunList,
		data: runList,
	} = useStoreQuery({
		selector: (store) => store.workflowRunsStore.facilityWorkflowRuns,
		stopOn: (value) => value !== null && value.queryKey === queryKey,
		query: () =>
			dispatch(
				fetchFacilityWorkflowRuns(
					new FetchFacilityWorkflowRuns(facilityId, listFilters.queryParams, queryKey),
				),
			),
		deps: [listFilters],
	});

	const { data: facilityUsers } = useStoreQuery({
		selector: (store) => store.facilityUsersStore.users,
		stopOn: (value) => value !== null && value.facilityId === facilityId,
		query: () => dispatch(fetchFacilityUsers(new FetchFacilityUsers(facilityId))),
	});

	useRefetchInterval(refetchRunList, SUPERVISION_REFETCH_INTERVAL_IN_MILLISECONDS);

	const isLoading = runList === null && listError === null;

	const handleRefreshClick = React.useCallback(() => {
		refetchRunList();
	}, [refetchRunList]);

	const users = React.useMemo(() => {
		if (!canSeeAllUsers || facilityUsers === null || facilityUsers.facilityId !== facilityId) {
			return [];
		}

		return facilityUsers.list;
	}, [canSeeAllUsers, facilityUsers, facilityId]);

	const workflowRunsCount = React.useMemo(() => {
		if (!runList) {
			return '0 traitements';
		}

		return `${runList.totalRecords} traitement${runList.totalRecords > 1 ? 's' : ''}`;
	}, [runList]);

	const eyebrow = React.useMemo(() => {
		if (!organizationName) {
			return;
		}

		return `${organizationName} - ${currentFacility.name}`;
	}, [organizationName, currentFacility]);

	return (
		<PageContainer scrollY={'auto'}>
			<WorkflowRunsNotifications />
			<BreadcrumbActions>
				<Button
					type="button"
					text
					label="Actualiser"
					icon={<RefreshCw size={16} />}
					onClick={handleRefreshClick}
				/>
			</BreadcrumbActions>
			<PageHeader eyebrow={eyebrow} title="Traitements" description={workflowRunsCount} />
			<Filters beneficiaries={beneficiaryDirectory?.entries ?? []} users={users} />
			<WorkflowRunsContent
				beneficiaryDirectory={beneficiaryDirectory}
				subjectDirectory={subjectDirectory}
				processingList={runList}
				isLoadingProcessingList={isLoading}
				processingListError={listError}
				refreshProcessingList={refetchRunList}
				isFetchingProcessingList={isFetching}
			/>
		</PageContainer>
	);
};

export default WorkflowRunsPage;
